import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { DataOptions } from "vuetify";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { BankAccount } from "@/models/api/administration/exportConfiguration/bankAccount";
import { ServiceType, TranslateToServiceType } from "@/models/api/administration/exportConfiguration/serviceType";
import { BankAccountExportConfiguration } from "@/models/api/administration/exportConfiguration/bankAccountExportConfiguration";
import { Integration } from "@/models/api/administration/exportConfiguration/integration";
import {
  getBankAccountList,
  getBankAccountExportConfiguration,
  postSaveBankAccountExportConfiguration,
  postDeleteBankAccountExportConfiguration,
  getIntegrationList,
  getServiceTypeList,
  getProgramList,
  getRegisterList,
  addNewServiceTypeTranslation,
  removeServiceTypeTranslation,
  editServiceTypeTranslation,
} from "@/requests/administration/administrationExportConfiguration";
import { FILTER_NAMES } from "@/static/filterNames";
import { SelectItem } from "@/models/api/SelectItem";
import { DocumentTypeRegister } from "@/models/api/administration/exportConfiguration/documentTypeRegister";

export interface AdministrationExportConfigurationStateInterface {
  isBankAccountTableLoading: boolean;
  bankAccountTableData: Array<BankAccount>;
  bankAccountTableOptions: DataOptions;
  bankAccountTableCount: number;
  bankAccountTableFilters: Array<CriterionItemRequest> | null;
  selectedBankAccountId: number | null;
  bankAccountExportConfiguration: BankAccountExportConfiguration | null;
  isAddingNew: boolean;

  integrationList: Array<Integration>;
  selectedIntegrationId: string | null;
  isOrderTableLoading: boolean;
  orderTableData: Array<ServiceType>;
  orderTableOptions: DataOptions;
  orderTableCount: number;
  orderTableFilters: Array<CriterionItemRequest> | null;

  programList: Array<SelectItem>;
  selectedProgramId: number | null;
  isRegisterTableLoading: boolean;
  registerTableData: Array<DocumentTypeRegister>;
  registerTableOptions: DataOptions;
  registerTableCount: number;
  registerTableFilters: Array<CriterionItemRequest> | null;
  recordStampId: number | null;
}

export const AdministrationExportConfigurationState: AdministrationExportConfigurationStateInterface = {
  isBankAccountTableLoading: false,
  bankAccountTableData: [],
  bankAccountTableOptions: new DefaultDataOptions(),
  bankAccountTableCount: -1,
  bankAccountTableFilters: null,
  selectedBankAccountId: null,
  bankAccountExportConfiguration: null,
  isAddingNew: false,

  integrationList: [],
  selectedIntegrationId: null,
  isOrderTableLoading: false,
  orderTableData: [],
  orderTableOptions: new DefaultDataOptions(),
  orderTableCount: -1,
  orderTableFilters: null,

  programList: [],
  selectedProgramId: null,
  isRegisterTableLoading: false,
  registerTableData: [],
  registerTableOptions: new DefaultDataOptions(),
  registerTableCount: -1,
  registerTableFilters: null,
  recordStampId: null,
};

const AdministrationExportConfigurationModule = defineModule({
  state: AdministrationExportConfigurationState as AdministrationExportConfigurationStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsBankAccountTableLoading(state, param: boolean) {
      state.isBankAccountTableLoading = param;
    },
    setBankAccountTableData(state, param: Array<BankAccount>) {
      state.bankAccountTableData = param;
    },
    setBankAccountTableOptions(state, param: DataOptions) {
      state.bankAccountTableOptions = param;
    },
    setBankAccountTableCount(state, param: number) {
      state.bankAccountTableCount = param;
    },
    setBankAccountTableFilters(state, param: Array<CriterionItemRequest> | null) {
      state.bankAccountTableFilters = param;
    },
    setSelectedBankAccountId(state, param: number | null) {
      state.selectedBankAccountId = param;
    },
    setBankAccountExportConfiguration(state, param: BankAccountExportConfiguration | null) {
      state.bankAccountExportConfiguration = param;
    },
    setIsAddingNew(state, param: boolean) {
      state.isAddingNew = param;
    },
    setIntegrationList(state, param: Array<Integration>) {
      state.integrationList = param;
    },
    setSelectedIntegrationId(state, param: string | null) {
      state.selectedIntegrationId = param;
    },
    setIsOrderTableLoading(state, param: boolean) {
      state.isOrderTableLoading = param;
    },
    setOrderTableData(state, param: Array<ServiceType>) {
      state.orderTableData = param;
    },
    setOrderTableOptions(state, param: DataOptions) {
      state.orderTableOptions = param;
    },
    setOrderTableCount(state, param: number) {
      state.orderTableCount = param;
    },
    setOrderTableFilters(state, param: Array<CriterionItemRequest> | null) {
      state.orderTableFilters = param;
    },
    setProgramList(state, param: Array<SelectItem>) {
      state.programList = param;
    },
    setSelectedProgramId(state, param: number | null) {
      state.selectedProgramId = param;
    },
    setIsRegisterTableLoading(state, param: boolean) {
      state.isRegisterTableLoading = param;
    },
    setRegisterTableData(state, param: Array<DocumentTypeRegister>) {
      state.registerTableData = param;
    },
    setRegisterTableOptions(state, param: DataOptions) {
      state.registerTableOptions = param;
    },
    setRegisterTableCount(state, param: number) {
      state.registerTableCount = param;
    },
    setRegisterTableFilters(state, param: Array<CriterionItemRequest> | null) {
      state.registerTableFilters = param;
    },
    setRecordStampId(state, param: number) {
      state.recordStampId = param;
    },
  },
  actions: {
    async downloadBankAccountList(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsBankAccountTableLoading(true);
      const response = await getBankAccountList({
        count: state.bankAccountTableOptions.itemsPerPage,
        page: state.bankAccountTableOptions.page - 1,
        criterions: state.bankAccountTableFilters,
        filterName: FILTER_NAMES.BankAccountExportConfigurationFilter,
      });
      commit.setBankAccountTableData(response.items);
      commit.setBankAccountTableCount(response.totalCount);
      commit.setIsBankAccountTableLoading(false);
      if (response.items.length > 0) {
        commit.setSelectedBankAccountId(response.items[0].id);
      } else {
        commit.setSelectedBankAccountId(null);
      }
    },
    async downloadBankAccountExportConfiguration(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedBankAccountId !== null) {
        const response = await getBankAccountExportConfiguration(state.selectedBankAccountId);
        commit.setBankAccountExportConfiguration(response);
      }
    },
    async saveBankAccountExportConfiguration(context) {
      const { state } = _moduleActionContext(context);
      if (state.selectedBankAccountId !== null && state.bankAccountExportConfiguration) {
        await postSaveBankAccountExportConfiguration(state.selectedBankAccountId, state.bankAccountExportConfiguration);
      }
    },
    async deleteBankAccountExportConfiguration(context) {
      const { state } = _moduleActionContext(context);
      if (state.selectedBankAccountId !== null && state.bankAccountExportConfiguration) {
        await postDeleteBankAccountExportConfiguration(state.selectedBankAccountId);
      }
    },
    async addNewServiceTypeTranslation(
      context,
      param: { integrationId: string; serviceTypeId: number; translateServiceType: TranslateToServiceType }
    ) {
      const { state, commit } = _moduleActionContext(context);
      const newId = await addNewServiceTypeTranslation(
        param.integrationId,
        param.serviceTypeId,
        param.translateServiceType
      );
      param.translateServiceType.id = newId;
      commit.setIntegrationList(await getIntegrationList());
      if (state.integrationList.length > 0) commit.setSelectedIntegrationId(state.integrationList[0].key);
    },
    async removeServiceTypeTranslation(context, translateCodeId: number) {
      await removeServiceTypeTranslation(translateCodeId);
    },
    async editServiceTypeTranslation(context, serviceTypeTranslate: TranslateToServiceType) {
      await editServiceTypeTranslation(serviceTypeTranslate.id, serviceTypeTranslate);
    },
    async downloadIntegrationList(context) {
      const { state, commit } = _moduleActionContext(context);
      const response = await getIntegrationList();
      commit.setIntegrationList(response);
      if (state.integrationList.length > 0) commit.setSelectedIntegrationId(state.integrationList[0].key);
    },
    async downloadServiceTypeList(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsOrderTableLoading(true);
      if (state.selectedIntegrationId !== null) {
        const response = await getServiceTypeList(state.selectedIntegrationId, {
          count: state.orderTableOptions.itemsPerPage,
          page: state.orderTableOptions.page - 1,
          criterions: state.orderTableFilters,
          filterName: FILTER_NAMES.ServiceTypeListFilter,
        });
        commit.setOrderTableData(response.items);
        commit.setOrderTableCount(response.totalCount);
      } else {
        commit.setOrderTableData([]);
        commit.setOrderTableCount(-1);
      }
      commit.setIsOrderTableLoading(false);
    },
    async downloadProgramList(context) {
      const { state, commit } = _moduleActionContext(context);
      const response = await getProgramList();
      commit.setProgramList(response);
      if (state.programList.length > 0) commit.setSelectedProgramId(state.programList[0].value);
    },
    async downloadRegisterList(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsRegisterTableLoading(true);
      if (state.selectedProgramId !== null) {
        const response = await getRegisterList(state.selectedProgramId, {
          count: state.registerTableOptions.itemsPerPage,
          page: state.registerTableOptions.page - 1,
          criterions: state.registerTableFilters,
          filterName: FILTER_NAMES.DocumentTypeFilter,
        });
        commit.setRegisterTableData(response.items);
        commit.setRegisterTableCount(response.totalCount);
      } else {
        commit.setRegisterTableData([]);
        commit.setRegisterTableCount(-1);
      }
      commit.setIsRegisterTableLoading(false);
    },
  },
});

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
export default AdministrationExportConfigurationModule;
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationExportConfigurationModule);
